/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type SVGProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * Black Magic filled diagonal down-right arrow (↘) — the brand arrow shown beside "Read More" links
 * (e.g. Article Hero). Sibling of {@link ArrowUpRightIcon}. Path is the exact Figma asset
 * ("Down Arrow Icon / Small", viewBox 0 0 12 12) and fills the box edge-to-edge, so the rendered size
 * IS the visible glyph size — unlike `lucide-react`'s stroked `ArrowDownRight`, which pads the glyph
 * inside a 24×24 grid. `fill="currentColor"` so it inherits the surrounding text color (and flips with
 * the Default Settings light/dark theme).
 */
export function ArrowDownRightIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true" className={cn('size-3', className)} {...props}>
            <path d="M9.58964 7.88427V0.852099H12V10.7948C11.9999 11.1143 11.8728 11.421 11.6469 11.6469C11.421 11.8728 11.1143 11.9999 10.7948 12H0.852099V9.58964H7.88545L0 1.7042L1.7042 0L9.58964 7.88427Z" />
        </svg>
    );
}
